import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../lib/auth';
import { settingsAPI, auditAPI, mollieAPI, inboxAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import {
  Settings, User, Link2, Shield, Activity, Check, Building,
  Landmark, FileText, CreditCard, Globe, Phone, Mail, Loader2,
  Upload, Trash2, ImageIcon, Wallet, RefreshCw, Plug, PlugZap, Copy,
  Inbox as InboxIcon, RotateCw, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

export default function SettingsPage() {
  const { user } = useAuth();
  const [form, setForm] = useState({
    name: '', company_name: '',
    tax_number: '', vat_id: '', tax_type: 'regelbesteuerung',
    street: '', zip_code: '', city: '', country: 'Deutschland',
    bank_name: '', iban: '', bic: '', account_holder: '',
    company_email: '', company_phone: '', company_website: '',
    default_payment_term_days: 14, invoice_footer_text: '',
    dunning_enabled: false, dunning_days_after_due: 7,
  });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [ibanCheck, setIbanCheck] = useState(null);
  const [ibanChecking, setIbanChecking] = useState(false);

  const loadSettings = useCallback(async () => {
    try {
      const [settingsRes, logsRes] = await Promise.all([
        settingsAPI.get(),
        auditAPI.list(),
      ]);
      const s = settingsRes.data;
      setForm({
        name: s.name || user?.name || '',
        company_name: s.company_name || user?.company_name || '',
        tax_number: s.tax_number || '',
        vat_id: s.vat_id || '',
        tax_type: s.tax_type || 'regelbesteuerung',
        street: s.street || '',
        zip_code: s.zip_code || '',
        city: s.city || '',
        country: s.country || 'Deutschland',
        bank_name: s.bank_name || '',
        iban: s.iban || '',
        bic: s.bic || '',
        account_holder: s.account_holder || '',
        company_email: s.company_email || user?.email || '',
        company_phone: s.company_phone || '',
        company_website: s.company_website || '',
        default_payment_term_days: s.default_payment_term_days || 14,
        invoice_footer_text: s.invoice_footer_text || '',
        dunning_enabled: !!s.dunning_enabled,
        dunning_days_after_due: s.dunning_days_after_due || 7,
      });
      setLogs(logsRes.data.logs || []);
      // Check if logo exists — fetch via authenticated API call as a Blob
      if (s.logo_filename || s.logo_path) {
        try {
          const blobRes = await settingsAPI.fetchLogoBlob();
          const url = URL.createObjectURL(blobRes.data);
          setLogoUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return url; });
        } catch {
          setLogoUrl(null);
        }
      }
    } catch {
      // defaults are fine
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  // IBAN live prüfen und Bankname/BIC automatisch ermitteln (entprellt).
  useEffect(() => {
    const raw = (form.iban || '').replace(/[\s\-_.]/g, '').toUpperCase();
    if (raw.length < 5) { setIbanCheck(null); return; }
    const timer = setTimeout(async () => {
      setIbanChecking(true);
      try {
        const res = await settingsAPI.ibanLookup(raw);
        setIbanCheck(res.data);
        if (res.data.valid) {
          setForm((prev) => {
            const patch = {};
            if (res.data.formatted && prev.iban !== res.data.formatted) patch.iban = res.data.formatted;
            if (res.data.bank_name && !prev.bank_name.trim()) patch.bank_name = res.data.bank_name;
            if (res.data.bic && !prev.bic.trim()) patch.bic = res.data.bic;
            return Object.keys(patch).length ? { ...prev, ...patch } : prev;
          });
        }
      } catch {
        setIbanCheck(null);
      } finally {
        setIbanChecking(false);
      }
    }, 600);
    return () => clearTimeout(timer);
  }, [form.iban]);

  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Nur Bilddateien erlaubt');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Logo darf maximal 2 MB groß sein');
      return;
    }
    setLogoUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await settingsAPI.uploadLogo(formData);
      // Refresh as authenticated blob (img tag can't carry JWT)
      const blobRes = await settingsAPI.fetchLogoBlob();
      const url = URL.createObjectURL(blobRes.data);
      setLogoUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return url; });
      toast.success('Logo hochgeladen');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Logo-Upload fehlgeschlagen');
    } finally {
      setLogoUploading(false);
    }
  };

  const handleLogoDelete = async () => {
    try {
      await settingsAPI.deleteLogo();
      setLogoUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
      toast.success('Logo entfernt');
    } catch {
      toast.error('Fehler beim Entfernen');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await settingsAPI.update(form);
      toast.success('Einstellungen gespeichert');
    } catch { toast.error('Fehler beim Speichern'); }
    finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="settings-loading">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="settings-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tight mb-2">Einstellungen</h1>
          <p className="text-sm sm:text-base text-muted-foreground">Firmendaten, Steuerinformationen und Konfiguration</p>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_15px_rgba(0,229,255,0.3)]"
          data-testid="save-settings-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
          <span className="hidden sm:inline">{saving ? 'Wird gespeichert...' : 'Alle Einstellungen speichern'}</span>
          <span className="sm:hidden">{saving ? 'Speichern...' : 'Speichern'}</span>
        </Button>
      </div>

      <Tabs defaultValue="firma" data-testid="settings-tabs">
        <TabsList className="bg-card border border-border/50 flex-wrap h-auto gap-1 p-1">
          <TabsTrigger value="firma" className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary text-xs sm:text-sm" data-testid="tab-firma">
            <Building className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Firmendaten</span>
            <span className="sm:hidden">Firma</span>
          </TabsTrigger>
          <TabsTrigger value="steuer" className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary text-xs sm:text-sm" data-testid="tab-steuer">
            <FileText className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Steuern & Recht</span>
            <span className="sm:hidden">Steuern</span>
          </TabsTrigger>
          <TabsTrigger value="bank" className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary text-xs sm:text-sm" data-testid="tab-bank">
            <Landmark className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Bankverbindung</span>
            <span className="sm:hidden">Bank</span>
          </TabsTrigger>
          <TabsTrigger value="rechnung" className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary text-xs sm:text-sm" data-testid="tab-rechnung">
            <CreditCard className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Rechnungen</span>
            <span className="sm:hidden">Rechn.</span>
          </TabsTrigger>
          <TabsTrigger value="zahlungen" className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary text-xs sm:text-sm" data-testid="tab-zahlungen">
            <Wallet className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Zahlungen</span>
            <span className="sm:hidden">Pay</span>
          </TabsTrigger>
          <TabsTrigger value="posteingang" className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary text-xs sm:text-sm" data-testid="tab-posteingang">
            <InboxIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">E-Mail Posteingang</span>
            <span className="sm:hidden">Inbox</span>
          </TabsTrigger>
          <TabsTrigger value="system" className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary text-xs sm:text-sm" data-testid="tab-system">
            <Settings className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
            System
          </TabsTrigger>
        </TabsList>

        {/* ==================== FIRMA TAB ==================== */}
        <TabsContent value="firma" className="mt-6 space-y-6">
          {/* Logo Upload */}
          <Card className="bg-card border border-border/50" data-testid="logo-upload-card">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <ImageIcon className="w-4 h-4 text-primary" />
                Firmenlogo
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-6 flex-wrap">
                {/* Logo Preview */}
                <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-xl border-2 border-dashed border-border/50 flex items-center justify-center overflow-hidden bg-accent/30 flex-shrink-0" data-testid="logo-preview">
                  {logoUrl ? (
                    <img src={logoUrl} alt="Firmenlogo" className="max-w-full max-h-full object-contain p-2" data-testid="logo-image" />
                  ) : (
                    <div className="text-center">
                      <ImageIcon className="w-8 h-8 text-muted-foreground/40 mx-auto mb-1" />
                      <span className="text-[10px] text-muted-foreground">Kein Logo</span>
                    </div>
                  )}
                </div>

                {/* Upload/Delete Actions */}
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">
                    Logo erscheint oben links auf allen PDF-Rechnungen. Max. 2 MB (PNG, JPG, SVG).
                  </p>
                  <div className="flex gap-2">
                    <label
                      htmlFor="logo-upload"
                      className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-primary/30 bg-primary/5 text-primary hover:bg-primary/10 cursor-pointer transition-colors text-sm ${logoUploading ? 'pointer-events-none opacity-50' : ''}`}
                      data-testid="logo-upload-btn"
                    >
                      {logoUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                      {logoUrl ? 'Logo ändern' : 'Logo hochladen'}
                    </label>
                    <input
                      id="logo-upload"
                      type="file"
                      accept="image/png,image/jpeg,image/svg+xml,image/webp"
                      className="hidden"
                      onChange={handleLogoUpload}
                      disabled={logoUploading}
                      data-testid="logo-upload-input"
                    />
                    {logoUrl && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleLogoDelete}
                        className="text-destructive hover:text-destructive/80 text-sm"
                        data-testid="logo-delete-btn"
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Entfernen
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border border-border/50" data-testid="company-info-card">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Building className="w-4 h-4 text-primary" />
                Unternehmensinformationen
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Inhaber / Geschäftsführer</Label>
                  <Input value={form.name} onChange={update('name')} placeholder="Max Mustermann" className="bg-background h-10" data-testid="settings-name-input" />
                </div>
                <div className="space-y-2">
                  <Label>Firmenname</Label>
                  <Input value={form.company_name} onChange={update('company_name')} placeholder="Muster GmbH" className="bg-background h-10" data-testid="settings-company-input" />
                </div>
              </div>

              <Separator className="bg-border/30" />

              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground font-medium uppercase tracking-wider">Firmenadresse</Label>
              </div>
              <div className="space-y-2">
                <Label>Straße & Hausnummer</Label>
                <Input value={form.street} onChange={update('street')} placeholder="Musterstraße 1" className="bg-background h-10" data-testid="settings-street-input" />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>PLZ</Label>
                  <Input value={form.zip_code} onChange={update('zip_code')} placeholder="10115" className="bg-background h-10" data-testid="settings-zip-input" />
                </div>
                <div className="space-y-2">
                  <Label>Ort</Label>
                  <Input value={form.city} onChange={update('city')} placeholder="Berlin" className="bg-background h-10" data-testid="settings-city-input" />
                </div>
                <div className="space-y-2">
                  <Label>Land</Label>
                  <Input value={form.country} onChange={update('country')} className="bg-background h-10" data-testid="settings-country-input" />
                </div>
              </div>

              <Separator className="bg-border/30" />

              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground font-medium uppercase tracking-wider">Kontaktdaten</Label>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-1.5"><Mail className="w-3 h-3" /> E-Mail</Label>
                  <Input type="email" value={form.company_email} onChange={update('company_email')} placeholder="info@firma.de" className="bg-background h-10" data-testid="settings-email-input" />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-1.5"><Phone className="w-3 h-3" /> Telefon</Label>
                  <Input value={form.company_phone} onChange={update('company_phone')} placeholder="+49 30 12345678" className="bg-background h-10" data-testid="settings-phone-input" />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-1.5"><Globe className="w-3 h-3" /> Website</Label>
                  <Input value={form.company_website} onChange={update('company_website')} placeholder="www.firma.de" className="bg-background h-10" data-testid="settings-website-input" />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ==================== STEUER TAB ==================== */}
        <TabsContent value="steuer" className="mt-6 space-y-6">
          <Card className="bg-card border border-border/50" data-testid="tax-info-card">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <FileText className="w-4 h-4 text-primary" />
                Steuerinformationen
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 text-sm" data-testid="tax-info-notice">
                <p className="text-amber-400 font-medium mb-1">Wichtig für korrekte Ausgangsrechnungen</p>
                <p className="text-muted-foreground">
                  Gemäß §14 UStG muss jede Rechnung entweder die Steuernummer oder die USt-IdNr. des Rechnungsstellers enthalten.
                  Beide Angaben sind auf Ihren Ausgangsrechnungen sichtbar.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="font-semibold">Steuernummer</Label>
                  <Input
                    value={form.tax_number}
                    onChange={update('tax_number')}
                    placeholder="z.B. 27/123/45678"
                    className="bg-background h-10 font-mono-data"
                    data-testid="settings-tax-number-input"
                  />
                  <p className="text-xs text-muted-foreground">Vom Finanzamt zugeteilte Steuernummer</p>
                </div>
                <div className="space-y-2">
                  <Label className="font-semibold">USt-IdNr. (Umsatzsteuer-Identifikationsnummer)</Label>
                  <Input
                    value={form.vat_id}
                    onChange={update('vat_id')}
                    placeholder="z.B. DE123456789"
                    className="bg-background h-10 font-mono-data"
                    data-testid="settings-vat-id-input"
                  />
                  <p className="text-xs text-muted-foreground">Für innergemeinschaftliche Leistungen erforderlich</p>
                </div>
              </div>

              <Separator className="bg-border/30" />

              <div className="space-y-2">
                <Label className="font-semibold">Besteuerungsart</Label>
                <Select value={form.tax_type} onValueChange={(v) => setForm({ ...form, tax_type: v })}>
                  <SelectTrigger className="bg-background h-10" data-testid="settings-tax-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="regelbesteuerung">Regelbesteuerung (§19 UStG Regelbesteuerer)</SelectItem>
                    <SelectItem value="kleinunternehmer">Kleinunternehmerregelung (§19 Abs. 1 UStG)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {form.tax_type === 'kleinunternehmer'
                    ? 'Als Kleinunternehmer wird keine Umsatzsteuer auf Rechnungen ausgewiesen.'
                    : 'Umsatzsteuer wird auf Rechnungen ausgewiesen und muss ans Finanzamt abgeführt werden.'
                  }
                </p>
              </div>

              {/* Preview of how it appears on invoice */}
              <div className="bg-accent/50 rounded-lg p-5 space-y-3" data-testid="tax-preview">
                <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">Vorschau: So erscheint es auf Ihren Rechnungen</p>
                <div className="border border-border/30 rounded-lg p-4 bg-background/50 space-y-1 text-sm">
                  <p className="font-bold">{form.company_name || 'Firmenname'}</p>
                  <p>{form.name || 'Inhaber'}</p>
                  <p>{form.street || 'Straße'}</p>
                  <p>{form.zip_code || 'PLZ'} {form.city || 'Ort'}</p>
                  <Separator className="my-2 bg-border/30" />
                  {form.tax_number && <p className="text-muted-foreground">Steuernr.: <span className="font-mono-data">{form.tax_number}</span></p>}
                  {form.vat_id && <p className="text-muted-foreground">USt-IdNr.: <span className="font-mono-data">{form.vat_id}</span></p>}
                  {!form.tax_number && !form.vat_id && (
                    <p className="text-destructive text-xs">Bitte Steuernummer oder USt-IdNr. hinterlegen!</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ==================== BANK TAB ==================== */}
        <TabsContent value="bank" className="mt-6 space-y-6">
          <Card className="bg-card border border-border/50" data-testid="bank-info-card">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Landmark className="w-4 h-4 text-primary" />
                Bankverbindung
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 text-sm text-muted-foreground">
                Ihre Bankverbindung wird auf Ausgangsrechnungen angezeigt, damit Kunden den Betrag überweisen können.
              </div>

              <div className="space-y-2">
                <Label>Bankname</Label>
                <Input value={form.bank_name} onChange={update('bank_name')} placeholder="wird aus der IBAN ermittelt" className="bg-background h-10" data-testid="settings-bank-name-input" />
              </div>
              <div className="space-y-2">
                <Label>Kontoinhaber</Label>
                <Input value={form.account_holder} onChange={update('account_holder')} placeholder="z.B. Max Mustermann oder Firma GmbH" className="bg-background h-10" data-testid="settings-account-holder-input" />
                <p className="text-xs text-muted-foreground">Falls abweichend vom Firmennamen. Wird auf Rechnungen und im QR-Code verwendet.</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>IBAN</Label>
                  <Input
                    value={form.iban}
                    onChange={update('iban')}
                    placeholder="DE89 3704 0044 0532 0130 00"
                    className="bg-background h-10 font-mono-data"
                    data-testid="settings-iban-input"
                  />
                  {ibanChecking && (
                    <p className="text-xs text-muted-foreground flex items-center gap-1.5" data-testid="iban-checking">
                      <Loader2 className="w-3 h-3 animate-spin" /> IBAN wird geprüft …
                    </p>
                  )}
                  {!ibanChecking && ibanCheck && !ibanCheck.valid && (
                    <p className="text-xs text-destructive flex items-center gap-1.5" data-testid="iban-invalid">
                      <AlertCircle className="w-3 h-3" /> {ibanCheck.error}
                    </p>
                  )}
                  {!ibanChecking && ibanCheck?.valid && (
                    <p className="text-xs text-emerald-400 flex items-center gap-1.5" data-testid="iban-valid">
                      <Check className="w-3 h-3" />
                      IBAN gültig{ibanCheck.country_name ? ` (${ibanCheck.country_name})` : ''}
                      {ibanCheck.bank_name ? ` · ${ibanCheck.bank_name}` : ''}
                      {ibanCheck.bank_city ? `, ${ibanCheck.bank_city}` : ''}
                    </p>
                  )}
                  {!ibanChecking && ibanCheck?.valid && ibanCheck.source === 'unknown' && (
                    <p className="text-xs text-muted-foreground" data-testid="iban-bank-unknown">
                      Zu dieser Bankleitzahl ist kein Bankname hinterlegt – bitte manuell eintragen.
                    </p>
                  )}
                  {!ibanChecking && ibanCheck?.valid && ibanCheck.source === 'unavailable' && (
                    <p className="text-xs text-muted-foreground" data-testid="iban-bank-unavailable">
                      Bankname konnte gerade nicht ermittelt werden – bitte manuell eintragen.
                    </p>
                  )}
                  {!ibanChecking && ibanCheck?.valid && ibanCheck.bank_name
                    && form.bank_name.trim() && form.bank_name.trim() !== ibanCheck.bank_name && (
                    <button
                      type="button"
                      onClick={() => setForm({
                        ...form,
                        bank_name: ibanCheck.bank_name,
                        bic: ibanCheck.bic || form.bic,
                      })}
                      className="text-xs text-primary underline-offset-2 hover:underline"
                      data-testid="iban-apply-bank-btn"
                    >
                      „{ibanCheck.bank_name}“ als Bankname übernehmen
                    </button>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>BIC</Label>
                  <Input
                    value={form.bic}
                    onChange={update('bic')}
                    placeholder="COBADEFFXXX"
                    className="bg-background h-10 font-mono-data"
                    data-testid="settings-bic-input"
                  />
                  {form.bic.trim() && !/^[A-Za-z]{6}[A-Za-z0-9]{2}([A-Za-z0-9]{3})?$/.test(form.bic.replace(/\s/g, '')) && (
                    <p className="text-xs text-destructive flex items-center gap-1.5" data-testid="bic-invalid">
                      <AlertCircle className="w-3 h-3" /> BIC muss 8 oder 11 Zeichen haben
                    </p>
                  )}
                  {ibanCheck?.valid && ibanCheck.bic && form.bic.replace(/\s/g, '').toUpperCase() !== ibanCheck.bic && (
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, bic: ibanCheck.bic })}
                      className="text-xs text-primary underline-offset-2 hover:underline"
                      data-testid="iban-apply-bic-btn"
                    >
                      BIC {ibanCheck.bic} aus der IBAN übernehmen
                    </button>
                  )}
                </div>
              </div>

              {/* Bank preview */}
              {form.iban && (
                <div className="bg-accent/50 rounded-lg p-4 space-y-1" data-testid="bank-preview">
                  <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider mb-2">Vorschau Zahlungsinformationen</p>
                  <div className="text-sm space-y-0.5">
                    {form.bank_name && <p>Bank: <span className="font-medium">{form.bank_name}</span></p>}
                    <p>IBAN: <span className="font-mono-data font-medium">{form.iban}</span></p>
                    {form.bic && <p>BIC: <span className="font-mono-data font-medium">{form.bic}</span></p>}
                    <p>Empfänger: <span className="font-medium">{form.account_holder || form.company_name || form.name}</span></p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ==================== RECHNUNG TAB ==================== */}
        <TabsContent value="rechnung" className="mt-6 space-y-6">
          <Card className="bg-card border border-border/50" data-testid="invoice-settings-card">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <CreditCard className="w-4 h-4 text-primary" />
                Rechnungseinstellungen
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="space-y-2">
                <Label>Standard-Zahlungsziel (Tage)</Label>
                <Input
                  type="number"
                  min="0"
                  max="365"
                  value={form.default_payment_term_days}
                  onChange={(e) => setForm({ ...form, default_payment_term_days: parseInt(e.target.value) || 14 })}
                  className="bg-background h-10 w-32 font-mono-data"
                  data-testid="settings-payment-term-input"
                />
                <p className="text-xs text-muted-foreground">
                  Das Fälligkeitsdatum wird automatisch berechnet: Rechnungsdatum + {form.default_payment_term_days} Tage
                </p>
              </div>

              <div className="space-y-2">
                <Label>Rechnungs-Fußtext</Label>
                <Textarea
                  value={form.invoice_footer_text}
                  onChange={update('invoice_footer_text')}
                  placeholder="z.B. Vielen Dank für Ihr Vertrauen! Bei Fragen kontaktieren Sie uns gerne."
                  className="bg-background min-h-[100px]"
                  data-testid="settings-footer-text-input"
                />
                <p className="text-xs text-muted-foreground">Dieser Text erscheint am Ende jeder Ausgangsrechnung</p>
              </div>

              {/* Automatische Zahlungserinnerung */}
              <div className="pt-4 border-t border-border/50 space-y-3" data-testid="dunning-settings-block">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <Label className="text-sm font-semibold">Automatische Zahlungserinnerung</Label>
                    <p className="text-xs text-muted-foreground mt-1">
                      Sendet einmalig eine freundliche Erinnerungsmail (ohne Mahngebühr) an den Kunden,
                      wenn eine überfällige Rechnung X Tage nach Fälligkeit noch offen ist.
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer flex-shrink-0" data-testid="dunning-enabled-toggle">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={form.dunning_enabled}
                      onChange={(e) => setForm({ ...form, dunning_enabled: e.target.checked })}
                    />
                    <div className="w-11 h-6 bg-muted rounded-full peer peer-focus:ring-2 peer-focus:ring-amber-500/40 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:border-border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500"></div>
                  </label>
                </div>
                {form.dunning_enabled && (
                  <div className="space-y-2 pl-1">
                    <Label className="text-xs text-muted-foreground">Tage nach Fälligkeit</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min="1"
                        max="90"
                        value={form.dunning_days_after_due}
                        onChange={(e) => setForm({ ...form, dunning_days_after_due: parseInt(e.target.value) || 7 })}
                        className="bg-background h-10 w-24 font-mono-data"
                        data-testid="dunning-days-input"
                      />
                      <span className="text-sm text-muted-foreground">Tage nach dem Fälligkeitsdatum</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Die Erinnerung wird pro Rechnung nur genau einmal automatisch verschickt.
                    </p>
                  </div>
                )}
              </div>

              {form.tax_type === 'kleinunternehmer' && (
                <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-4 text-sm">
                  <p className="text-amber-400 font-medium mb-1">Hinweis für Kleinunternehmer</p>
                  <p className="text-muted-foreground">
                    Als Kleinunternehmer muss auf jeder Rechnung folgender Vermerk stehen: 
                    "Gemäß §19 UStG wird keine Umsatzsteuer berechnet." 
                    Dieser Hinweis wird automatisch auf Ihren Rechnungen eingefügt.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ==================== ZAHLUNGEN TAB (Mollie) ==================== */}
        <TabsContent value="zahlungen" className="mt-6 space-y-6">
          <MollieIntegrationCard />
        </TabsContent>

        {/* ==================== POSTEINGANG TAB (Inbox) ==================== */}
        <TabsContent value="posteingang" className="mt-6 space-y-6">
          <InboxIntegrationCard />
        </TabsContent>

        {/* ==================== SYSTEM TAB ==================== */}
        <TabsContent value="system" className="mt-6 space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Integrations */}
            <Card className="bg-card border border-border/50" data-testid="integrations-card">
              <CardHeader>
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <Link2 className="w-4 h-4 text-secondary" />
                  Integrationen
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {[
                  { name: 'Gemini Flash', desc: 'OCR / KI-Erkennung', ok: true },
                  { name: 'Resend', desc: 'E-Mail-Versand', ok: true },
                ].map((int) => (
                  <div key={int.name} className="flex items-center justify-between py-3 border-b border-border/30 last:border-0">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-lg ${int.ok ? 'bg-emerald-500/10' : 'bg-red-500/10'} flex items-center justify-center`}>
                        <Check className={`w-4 h-4 ${int.ok ? 'text-emerald-400' : 'text-red-400'}`} />
                      </div>
                      <div>
                        <p className="text-sm font-medium">{int.name}</p>
                        <p className="text-xs text-muted-foreground">{int.desc}</p>
                      </div>
                    </div>
                    <Badge className={`${int.ok ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-red-500/10 text-red-400 border-red-500/20'}`}>
                      {int.ok ? 'Verbunden' : 'Offline'}
                    </Badge>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Security */}
            <Card className="bg-card border border-border/50" data-testid="security-card">
              <CardHeader>
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <Shield className="w-4 h-4 text-amber-400" />
                  Sicherheit & Compliance
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm text-muted-foreground">
                {[
                  'JWT-Authentifizierung aktiv',
                  'DSGVO-konforme Datenhaltung',
                  'Audit-Logging aktiviert',
                  'Verschlüsselte API-Kommunikation',
                  'Steuerkonforme Exporte (Anlage EÜR, USt 2 A, UStVA)',
                ].map((item) => (
                  <div key={item} className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-emerald-500" />
                    <span>{item}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>

          {/* Audit Log */}
          <Card className="bg-card border border-border/50" data-testid="audit-log-card">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Activity className="w-4 h-4 text-primary" />
                Audit-Log (letzte Aktionen)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {logs.length === 0 ? (
                <p className="text-sm text-muted-foreground py-6 text-center">Noch keine Aktivitäten</p>
              ) : (
                <div className="space-y-2">
                  {logs.slice(0, 15).map((log) => (
                    <div key={log.id} className="flex items-center justify-between py-2 border-b border-border/20 last:border-0">
                      <div className="flex items-center gap-3">
                        <div className="w-2 h-2 rounded-full bg-primary/50" />
                        <span className="text-sm">{log.details}</span>
                      </div>
                      <span className="text-xs text-muted-foreground font-mono-data">
                        {new Date(log.timestamp).toLocaleString('de-DE')}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ============================ Mollie Integration Card ============================

function MollieIntegrationCard() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState('');
  const [testmode, setTestmode] = useState(true);
  const [autoSend, setAutoSend] = useState(false);
  const [taxRate, setTaxRate] = useState(19);
  const [descPrefix, setDescPrefix] = useState('Mollie-Zahlung');
  const [connecting, setConnecting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await mollieAPI.status();
      setStatus(res.data);
      if (res.data.connected) {
        setTestmode(res.data.testmode);
        setAutoSend(res.data.auto_send_email);
        setTaxRate(res.data.default_tax_rate);
        setDescPrefix(res.data.invoice_description_prefix || 'Mollie-Zahlung');
      }
    } catch {
      toast.error('Mollie-Status konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleConnect = async () => {
    if (!token.trim()) { toast.error('Bitte Access Token eingeben'); return; }
    setConnecting(true);
    try {
      const res = await mollieAPI.connect({
        access_token: token.trim(),
        testmode,
        auto_send_email: autoSend,
        default_tax_rate: Number(taxRate),
        invoice_description_prefix: descPrefix,
      });
      const profiles = res.data.profiles || [];
      toast.success(`Mollie verbunden — ${profiles.length} Profil${profiles.length === 1 ? '' : 'e'} gefunden`);
      setToken('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Verbindung fehlgeschlagen');
    } finally {
      setConnecting(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await mollieAPI.sync(90);
      const d = res.data;
      toast.success(`Sync: ${d.imported} importiert, ${d.skipped_existing} bereits vorhanden, ${d.skipped_unpaid} unbezahlt`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sync fehlgeschlagen');
    } finally {
      setSyncing(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Mollie wirklich trennen? Der Access Token wird gelöscht.')) return;
    setDisconnecting(true);
    try {
      await mollieAPI.disconnect();
      toast.success('Mollie getrennt');
      load();
    } catch {
      toast.error('Trennen fehlgeschlagen');
    } finally {
      setDisconnecting(false);
    }
  };

  const handleUpdate = async (changes) => {
    setConnecting(true);
    try {
      await mollieAPI.updateSettings(changes);
      toast.success('Einstellungen aktualisiert');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Aktualisierung fehlgeschlagen');
    } finally {
      setConnecting(false);
    }
  };

  const copyWebhookUrl = () => {
    if (!status?.webhook_url) return;
    navigator.clipboard.writeText(status.webhook_url);
    toast.success('Webhook-URL kopiert');
  };

  if (loading) {
    return (
      <Card className="bg-card border border-border/50">
        <CardContent className="py-12 flex justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="bg-card border border-border/50" data-testid="mollie-card">
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Wallet className="w-5 h-5 text-primary" />
              Mollie — Zahlungs­integration
            </div>
            {status?.connected ? (
              <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20" data-testid="mollie-status-badge">
                <PlugZap className="w-3 h-3 mr-1" /> Verbunden ({status.testmode ? 'Test' : 'Live'})
              </Badge>
            ) : (
              <Badge className="bg-slate-500/10 text-slate-400 border-slate-500/20" data-testid="mollie-status-badge">
                <Plug className="w-3 h-3 mr-1" /> Nicht verbunden
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {!status?.connected ? (
            <>
              <div className="text-sm text-muted-foreground">
                Verbinde dein Mollie-Konto, um eingehende Zahlungen automatisch als Rechnungen in LAIzyFinance zu erfassen.
                Du findest deinen <strong>Organization Access Token</strong> im Mollie-Dashboard unter <em>Developers → Organization access tokens</em>.
              </div>

              <div className="space-y-2">
                <Label>Mollie Access Token *</Label>
                <Input
                  type="password"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  placeholder="access_..."
                  className="bg-background font-mono-data"
                  data-testid="mollie-token-input"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Standard USt-Satz (%)</Label>
                  <Input
                    type="number"
                    value={taxRate}
                    onChange={(e) => setTaxRate(e.target.value)}
                    className="bg-background"
                    data-testid="mollie-tax-rate-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Rechnungs­beschreibung (Präfix)</Label>
                  <Input
                    value={descPrefix}
                    onChange={(e) => setDescPrefix(e.target.value)}
                    className="bg-background"
                    data-testid="mollie-desc-prefix-input"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between p-3 rounded-lg bg-accent/40 border border-border/30">
                <div>
                  <p className="text-sm font-medium">Test Mode</p>
                  <p className="text-xs text-muted-foreground">Wenn aktiv: nur Test-Zahlungen werden verarbeitet.</p>
                </div>
                <Switch checked={testmode} onCheckedChange={setTestmode} data-testid="mollie-testmode-switch" />
              </div>

              <div className="flex items-center justify-between p-3 rounded-lg bg-accent/40 border border-border/30">
                <div>
                  <p className="text-sm font-medium">Rechnungen automatisch versenden</p>
                  <p className="text-xs text-muted-foreground">Aus = nur Entwurf erstellen. An = automatisch per E-Mail an den Kunden schicken.</p>
                </div>
                <Switch checked={autoSend} onCheckedChange={setAutoSend} data-testid="mollie-autosend-switch" />
              </div>

              <Button
                onClick={handleConnect}
                disabled={connecting || !token.trim()}
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
                data-testid="mollie-connect-btn"
              >
                {connecting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PlugZap className="w-4 h-4 mr-2" />}
                Mollie verbinden
              </Button>
            </>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-3 rounded-lg bg-accent/40 border border-border/30 space-y-1">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Access Token</p>
                  <p className="text-sm font-mono-data" data-testid="mollie-token-masked">{status.token_masked}</p>
                </div>
                <div className="p-3 rounded-lg bg-accent/40 border border-border/30 space-y-1">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Verbunden seit</p>
                  <p className="text-sm font-mono-data">{status.connected_at ? new Date(status.connected_at).toLocaleString('de-DE') : '—'}</p>
                </div>
                <div className="p-3 rounded-lg bg-accent/40 border border-border/30 space-y-1">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Modus</p>
                  <p className="text-sm font-semibold">{status.testmode ? 'Test Mode' : 'Live Mode'}</p>
                </div>
                <div className="p-3 rounded-lg bg-accent/40 border border-border/30 space-y-1">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Auto-Versand</p>
                  <p className="text-sm font-semibold">{status.auto_send_email ? 'An' : 'Aus (nur Entwurf)'}</p>
                </div>
                <div className="p-3 rounded-lg bg-accent/40 border border-border/30 space-y-1">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Webhook-ID</p>
                  <p className="text-sm font-mono-data" data-testid="mollie-webhook-id">{status.webhook_id || <span className="text-amber-400">— Nicht gesetzt —</span>}</p>
                </div>
                <div className="p-3 rounded-lg bg-accent/40 border border-border/30 space-y-1">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Signing Secret</p>
                  <p className="text-sm font-mono-data" data-testid="mollie-secret-masked">
                    {status.signing_secret_set ? status.signing_secret_masked : <span className="text-red-400">— Nicht gesetzt (unsicher!) —</span>}
                  </p>
                </div>
              </div>

              <Separator />

              <details className="rounded-lg bg-accent/20 border border-border/30 p-3">
                <summary className="cursor-pointer text-sm font-medium flex items-center gap-2" data-testid="mollie-edit-toggle">
                  <Settings className="w-4 h-4 text-muted-foreground" />
                  Webhook & Einstellungen bearbeiten
                </summary>
                <div className="space-y-4 mt-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>Webhook-ID (von Mollie Dashboard)</Label>
                      <Input
                        placeholder="hook_..."
                        defaultValue={status.webhook_id || ''}
                        onBlur={(e) => {
                          const v = e.target.value.trim();
                          if (v && v !== status.webhook_id) handleUpdate({ webhook_id: v });
                        }}
                        className="bg-background font-mono-data"
                        data-testid="mollie-webhook-id-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Signing Secret (whs_... oder ähnlich)</Label>
                      <Input
                        type="password"
                        placeholder={status.signing_secret_set ? '••• gespeichert — neu eingeben zum Ändern' : 'Aus Mollie Webhooks-Settings'}
                        onBlur={(e) => {
                          const v = e.target.value.trim();
                          if (v) handleUpdate({ signing_secret: v });
                        }}
                        className="bg-background font-mono-data"
                        data-testid="mollie-signing-secret-input"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>Standard USt-Satz (%)</Label>
                      <Input
                        type="number"
                        defaultValue={status.default_tax_rate}
                        onBlur={(e) => {
                          const v = Number(e.target.value);
                          if (v !== status.default_tax_rate) handleUpdate({ default_tax_rate: v });
                        }}
                        className="bg-background"
                        data-testid="mollie-edit-tax-rate"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Beschreibung Präfix</Label>
                      <Input
                        defaultValue={status.invoice_description_prefix}
                        onBlur={(e) => {
                          const v = e.target.value;
                          if (v !== status.invoice_description_prefix) handleUpdate({ invoice_description_prefix: v });
                        }}
                        className="bg-background"
                        data-testid="mollie-edit-prefix"
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-3 rounded-lg bg-accent/40 border border-border/30">
                    <div>
                      <p className="text-sm font-medium">Rechnung automatisch per E-Mail an Kunden senden</p>
                      <p className="text-xs text-muted-foreground">Wenn an: PDF wird sofort nach Zahlung an die Kunden-E-Mail geschickt.</p>
                    </div>
                    <Switch
                      checked={status.auto_send_email}
                      onCheckedChange={(v) => handleUpdate({ auto_send_email: v })}
                      data-testid="mollie-edit-autosend"
                    />
                  </div>

                  <div className="flex items-center justify-between p-3 rounded-lg bg-accent/40 border border-border/30">
                    <div>
                      <p className="text-sm font-medium">Test Mode</p>
                      <p className="text-xs text-muted-foreground">Aktiv = nur Mollie-Testzahlungen werden verarbeitet.</p>
                    </div>
                    <Switch
                      checked={status.testmode}
                      onCheckedChange={(v) => handleUpdate({ testmode: v })}
                      data-testid="mollie-edit-testmode"
                    />
                  </div>
                </div>
              </details>

              <Separator />

              <div className="space-y-2">
                <Label>Dein Webhook-URL für Mollie</Label>
                <div className="flex gap-2">
                  <Input
                    value={status.webhook_url}
                    readOnly
                    className="bg-background font-mono-data text-xs"
                    data-testid="mollie-webhook-url"
                  />
                  <Button type="button" variant="outline" onClick={copyWebhookUrl} data-testid="mollie-copy-webhook">
                    <Copy className="w-3 h-3" />
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Trage diese URL als <code className="text-primary">webhookUrl</code> beim Erstellen von Mollie-Zahlungen/Abos ein, damit Rechnungen automatisch erstellt werden.
                </p>
              </div>

              <Separator />

              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={handleSync}
                  disabled={syncing}
                  variant="outline"
                  className="flex-1"
                  data-testid="mollie-sync-btn"
                >
                  {syncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                  Letzte 90 Tage importieren
                </Button>
                <Button
                  onClick={handleDisconnect}
                  disabled={disconnecting}
                  variant="outline"
                  className="text-destructive hover:bg-destructive/10"
                  data-testid="mollie-disconnect-btn"
                >
                  {disconnecting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plug className="w-4 h-4 mr-2" />}
                  Trennen
                </Button>
              </div>

              <div className="text-xs text-muted-foreground border-l-2 border-primary/30 pl-3 italic">
                Sicherheitstipp: Rotiere deinen Mollie-Token regelmäßig (Mollie Dashboard → Developers → Organization Access Tokens).
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </>
  );
}

function InboxIntegrationCard() {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [emails, setEmails] = useState([]);
  const [emailsLoading, setEmailsLoading] = useState(false);

  const loadAll = useCallback(async () => {
    setEmailsLoading(true);
    try {
      const [infoRes, listRes] = await Promise.all([
        inboxAPI.info(),
        inboxAPI.list(),
      ]);
      setInfo(infoRes.data);
      setEmails(listRes.data.emails || []);
    } catch (e) {
      toast.error('Posteingang konnte nicht geladen werden');
    } finally {
      setLoading(false);
      setEmailsLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleCopy = async () => {
    if (!info?.address) return;
    try {
      await navigator.clipboard.writeText(info.address);
      toast.success('E-Mail-Adresse kopiert');
    } catch {
      toast.error('Kopieren fehlgeschlagen');
    }
  };

  const handleRegenerate = async () => {
    if (!window.confirm('Neue Posteingangs-Adresse erzeugen? Die alte Adresse funktioniert dann nicht mehr.')) return;
    setRegenerating(true);
    try {
      const res = await inboxAPI.regenerate();
      setInfo(res.data);
      toast.success('Neue Adresse erzeugt');
    } catch (e) {
      toast.error('Regenerieren fehlgeschlagen');
    } finally {
      setRegenerating(false);
    }
  };

  if (loading) {
    return (
      <Card className="bg-card border border-border/50">
        <CardContent className="py-10 flex justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="bg-card border border-border/50" data-testid="inbox-address-card">
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <InboxIcon className="w-4 h-4 text-primary" />
            Deine persönliche Posteingangs-Adresse
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Leite Lieferantenrechnungen an diese Adresse weiter. PDFs werden automatisch per OCR
            analysiert, Lieferanten ggf. angelegt und als Eingangsrechnung gespeichert.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
            <div
              className="flex-1 font-mono-data text-sm bg-background border border-border/50 rounded-lg px-3 py-2.5 break-all"
              data-testid="inbox-address-value"
            >
              {info?.address || '—'}
            </div>
            <Button
              onClick={handleCopy}
              variant="outline"
              className="border-primary/30 text-primary hover:bg-primary/10"
              data-testid="inbox-copy-btn"
            >
              <Copy className="w-4 h-4 mr-2" />
              Kopieren
            </Button>
            <Button
              onClick={handleRegenerate}
              disabled={regenerating}
              variant="outline"
              className="text-amber-400 border-amber-500/30 hover:bg-amber-500/10"
              data-testid="inbox-regenerate-btn"
            >
              {regenerating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RotateCw className="w-4 h-4 mr-2" />}
              Neu erzeugen
            </Button>
          </div>

          <div className="text-xs text-muted-foreground space-y-2 border-l-2 border-primary/30 pl-3">
            <p><strong>So funktioniert es:</strong></p>
            <ol className="list-decimal ml-4 space-y-1">
              <li>Trage diese Adresse als Rechnungsempfänger bei deinen Lieferanten ein oder leite einzelne Rechnungen weiter.</li>
              <li>Eingehende E-Mails mit PDF-Anhang werden via OCR analysiert.</li>
              <li>Unbekannte Lieferanten werden automatisch in deinen Kontakten angelegt.</li>
              <li>Du erhältst eine Benachrichtigung per E-Mail mit allen verarbeiteten Rechnungen.</li>
            </ol>
          </div>

          <div className="flex items-start gap-2 text-xs bg-amber-500/5 border border-amber-500/20 rounded-lg p-3">
            <AlertCircle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
            <span>
              Hinweis: Diese Adresse ist personalisiert. Teile sie nicht öffentlich – sie kann jederzeit oben neu erzeugt werden.
            </span>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-card border border-border/50" data-testid="inbox-log-card">
        <CardHeader className="flex flex-row items-center justify-between gap-2">
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Mail className="w-4 h-4 text-secondary" />
            Eingegangene E-Mails (letzte 100)
          </CardTitle>
          <Button
            onClick={loadAll}
            variant="ghost"
            size="sm"
            className="text-muted-foreground hover:text-primary"
            data-testid="inbox-refresh-btn"
          >
            {emailsLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
        </CardHeader>
        <CardContent>
          {emails.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              Noch keine E-Mails empfangen.
            </p>
          ) : (
            <div className="space-y-2">
              {emails.map((mail) => (
                <div
                  key={mail.id}
                  className="flex items-center justify-between gap-3 py-3 px-3 border border-border/30 rounded-lg hover:border-primary/30 transition-colors"
                  data-testid={`inbox-mail-row-${mail.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-medium truncate">{mail.subject || '(kein Betreff)'}</span>
                      <Badge
                        className={
                          mail.status === 'processed'
                            ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                            : 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20'
                        }
                      >
                        {mail.status === 'processed' ? 'Verarbeitet' : 'Kein Anhang'}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground truncate">
                      Von: <span className="font-mono-data">{mail.from_email}</span> ·{' '}
                      {new Date(mail.received_at).toLocaleString('de-DE')} ·{' '}
                      {mail.processed_invoices?.length || 0} Rechnung(en)
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
}

